// Pin configurations for the self-storage facility points layer. The default
// ("by pricing") keeps the icon-image expression that ships in the backend
// points metadata. The "by listing status" config recolors pins by whether a
// facility is listed on the platform, on-market externally, or off-market —
// for both operating facilities and development/construction sites.
// The "by type" config (VARIANT) shows operating = green circle, development =
// yellow square regardless of market status, and adds an optional orange outline
// highlight keyed to a user-selected metric.
const FacilityPinConfig = {
    PRICING: 'pricing',
    LISTING_STATUS: 'listing-status',
    BY_TYPE: 'by-type',
    // "Classic" arm of the adoption A/B: revives the original category-based
    // teardrop pins that ship in the points metadata (restore-to-original via
    // setFacilityPinIconImage(null)).
    LEGACY: 'legacy',
};

// Human-readable labels per pin config, shared by the legend menu and the
// Mixpanel switch/view events. 'Classic' = revived teardrops, 'Beta' = by-type.
export const PIN_CONFIG_LABELS = {
    [FacilityPinConfig.LEGACY]: 'Classic',
    [FacilityPinConfig.PRICING]: 'Pricing',
    [FacilityPinConfig.LISTING_STATUS]: 'Listing Status',
    [FacilityPinConfig.BY_TYPE]: 'Beta',
};

// Marker sprite ids registered in CustomMapMarkersHelper / MarkerSvgElements.
// Operating facilities use the warehouse glyph; development sites use the
// construction glyph. Colors: dark green (listed), green (on-market external),
// purple (off-market).
export const LISTING_STATUS_MARKERS = {
    operating: {
        listed: 'warehouse_listed',
        onMarket: 'warehouse_onmarket',
        offMarket: 'warehouse_offmarket',
    },
    development: {
        listed: 'development_listed',
        onMarket: 'development_onmarket',
        offMarket: 'development_offmarket',
    },
};

// Swatch colors used by the legend (must stay in sync with the marker SVGs in
// MarkerSvgElements.js). Exact values from the 1.2 Figma design (node
// 14849-114860): Listed on TractIQ = dark blue, Likely On Market = green,
// Off Market = purple.
export const LISTING_STATUS_COLORS = {
    listed: '#1e328f',
    onMarket: '#178f65',
    offMarket: '#83388a',
};

// Fill for development-site (square) pins. They use a yellow fill instead of white
// so the development treatment pops clearly on the map while keeping the
// status-colored outline + glyph unchanged. Value from the TractIQ 2025 Figma
// (node 14917-146207). Single source of truth — referenced by both the marker SVGs
// (MarkerSvgElements.js) and the legend swatch so the two never drift (same
// discipline as LISTING_STATUS_COLORS).
export const DEVELOPMENT_PIN_FILL = '#F3C14F';

// Derived feature property used to show/hide listing-status legend rows via
// the same virtual-layer filtering the default legend uses with `category`
// (see PointsHandler#applyGeoJsonLayerUpdate). Written onto each feature by
// GeoJsonLayersData#mergeFeatureListingFlags.
export const LISTING_STATUS_LAYER_PROPERTY = 'listing_status_layer';

// Virtual layer ids per legend row — `${group}-${status}`.
export const LISTING_STATUS_LAYER_IDS = {
    operating: {
        listed: 'operating-listed',
        onMarket: 'operating-onmarket',
        offMarket: 'operating-offmarket',
    },
    development: {
        listed: 'development-listed',
        onMarket: 'development-onmarket',
        offMarket: 'development-offmarket',
    },
};

// Mapbox `icon-image` expression for the "by listing status" config. Reads
// `has_listing` (merged client-side from the listings endpoint), `on_market`
// and `category` (already present on each facility feature).
// Note: Mapbox's `==` requires both operands to share a concrete type, so the
// `value`-typed `get` lookups are coerced via the `boolean` assertion (with a
// `false` fallback for missing/null properties). The category branches use
// `match`, which accepts `value` input directly (same as DealListPinsData).
export const LISTING_STATUS_ICON_IMAGE_EXPRESSION = [
    'case',
    ['boolean', ['get', 'has_listing'], false],
    [
        'match',
        ['get', 'category'],
        'construction',
        LISTING_STATUS_MARKERS.development.listed,
        LISTING_STATUS_MARKERS.operating.listed,
    ],
    ['boolean', ['get', 'on_market'], false],
    [
        'match',
        ['get', 'category'],
        'construction',
        LISTING_STATUS_MARKERS.development.onMarket,
        LISTING_STATUS_MARKERS.operating.onMarket,
    ],
    [
        'match',
        ['get', 'category'],
        'construction',
        LISTING_STATUS_MARKERS.development.offMarket,
        LISTING_STATUS_MARKERS.operating.offMarket,
    ],
];

// --- Pricing color mode (1.2 design) ---------------------------------------
// Same pin structure + styling as listing status, but the color encodes whether
// a facility has pricing. Has Pricing = green, No Pricing = purple — reusing the
// listing-status green/purple values so the two modes are visually identical.
export const PRICING_COLORS = {
    hasPricing: LISTING_STATUS_COLORS.onMarket, // #178f65
    noPricing: LISTING_STATUS_COLORS.offMarket, // #83388a
};

// Pricing reuses the green/purple listing-status marker sprites (same circle /
// square, 1px ring, 56px size) so the modes stay consistent and we avoid
// duplicating SVGs. Development sites are under construction (no operational
// rates), so they only ever render as No Pricing.
export const PRICING_MARKERS = {
    operating: {
        hasPricing: LISTING_STATUS_MARKERS.operating.onMarket, // green circle
        noPricing: LISTING_STATUS_MARKERS.operating.offMarket, // purple circle
    },
    development: {
        noPricing: LISTING_STATUS_MARKERS.development.offMarket, // purple square
    },
};

// Pricing pins are colored purely from `category`, which already ships on every
// facility feature (no id fetch / merge needed, unlike listing status):
//   'pricing'      → operating facility with pricing  → green circle
//   'nopricing'    → operating facility without pricing → purple circle
//   'construction' → development site (no pricing)     → purple square
// `category` is also the legend's filter property, so the legend rows map 1:1 to
// these values for show/hide.
export const PRICING_ICON_IMAGE_EXPRESSION = [
    'match',
    ['get', 'category'],
    'pricing',
    PRICING_MARKERS.operating.hasPricing,
    'nopricing',
    PRICING_MARKERS.operating.noPricing,
    'construction',
    PRICING_MARKERS.development.noPricing,
    // Fallback for any unexpected/non-self-storage category — neutral No Pricing.
    PRICING_MARKERS.operating.noPricing,
];

// --- "By type" config (VARIANT) -------------------------------------------
// Primary dimension: facility TYPE only (no status / pricing color on the base
// pin). Operating → solid green circle; development → solid yellow square.
// Plain variants have no icon or inner outline. Highlighted variants add a 3px
// orange outline on the shape itself (same orange as HIGHLIGHT_COLOR).
export const BY_TYPE_MARKERS = {
    operating: 'warehouse_bytype_plain',
    operatingHighlighted: 'warehouse_bytype_highlighted',
    development: 'development_bytype_plain',
    developmentHighlighted: 'development_bytype_highlighted',
};

// Mapbox icon-image expression: construction → yellow square, everything else
// → green circle. `category` is already present on every feature.
export const BY_TYPE_ICON_IMAGE_EXPRESSION = [
    'match',
    ['get', 'category'],
    'construction',
    BY_TYPE_MARKERS.development,
    BY_TYPE_MARKERS.operating,
];

// Returns an icon-image expression that shows highlighted sprites for features
// matching highlightFilter, plain sprites for all others.
export function byTypeIconImageWithHighlight(highlightFilter) {
    return [
        'case',
        highlightFilter,
        [
            'match',
            ['get', 'category'],
            'construction',
            BY_TYPE_MARKERS.developmentHighlighted,
            BY_TYPE_MARKERS.operatingHighlighted,
        ],
        [
            'match',
            ['get', 'category'],
            'construction',
            BY_TYPE_MARKERS.development,
            BY_TYPE_MARKERS.operating,
        ],
    ];
}

// Metric options for the orange outline highlight.
export const BY_TYPE_HIGHLIGHT_METRICS = {
    LISTED: 'listed',
    ON_MARKET: 'on-market',
    HAS_PRICING: 'has-pricing',
};

// Orange highlight outline color — single source of truth, used by both the
// highlighted marker sprites and the legend swatch so the two never drift.
export const HIGHLIGHT_COLOR = '#F5821F';

// Filter expressions for the highlight metrics. The orange outline is applied
// only on features where the selected metric evaluates to true.
//   on-market: `on_market` is normalized to a real boolean by
//     mergeFeatureListingFlags (handles Postgres 't' string).
//   has-pricing: `category === 'pricing'` (already on every facility feature).
export const BY_TYPE_HIGHLIGHT_FILTER_EXPRESSIONS = {
    // "Listed on TractIQ" reuses the listing-status "listed" condition (the
    // client-side-merged has_listing boolean) so the two surfaces agree.
    [BY_TYPE_HIGHLIGHT_METRICS.LISTED]: ['boolean', ['get', 'has_listing'], false],
    [BY_TYPE_HIGHLIGHT_METRICS.ON_MARKET]: ['boolean', ['get', 'on_market'], false],
    [BY_TYPE_HIGHLIGHT_METRICS.HAS_PRICING]: ['match', ['get', 'category'], 'pricing', true, false],
};

// Derived feature property for the by-type legend's show/hide, which is
// two-dimensional (facility type × highlighted-or-not). The legend filter matches a
// single property against a list of visible virtual-layer ids
// (PointsHandler#applyGeoJsonLayerUpdate), so the two dimensions are folded into one
// derived value — same pattern as LISTING_STATUS_LAYER_PROPERTY. Written onto each
// feature by GeoJsonLayersData#mergeByTypeLayerIds.
export const BY_TYPE_LAYER_PROPERTY = 'by_type_layer';

// Virtual layer ids per legend row — `${type}-${highlighted ? 'highlighted' : 'plain'}`.
// With no highlight metric selected every feature is `plain`, so the two plain rows then
// cover everything of their type (the legend's default two-row state).
export const BY_TYPE_LAYER_IDS = {
    operating: { highlighted: 'operating-highlighted', plain: 'operating-plain' },
    development: { highlighted: 'development-highlighted', plain: 'development-plain' },
};

// JS mirrors of BY_TYPE_HIGHLIGHT_FILTER_EXPRESSIONS above: the mapbox expressions decide
// which sprite a feature draws with, these decide which virtual layer it belongs to for
// show/hide. The two MUST stay in sync or a pin will be hidden by a row it doesn't match
// (same single-source discipline as LISTING_STATUS_COLORS ↔ the marker SVGs). Both read
// the flags normalized by mergeFeatureListingFlags.
export const BY_TYPE_HIGHLIGHT_PREDICATES = {
    [BY_TYPE_HIGHLIGHT_METRICS.LISTED]: properties => properties.has_listing === true,
    [BY_TYPE_HIGHLIGHT_METRICS.ON_MARKET]: properties => properties.on_market === true,
    [BY_TYPE_HIGHLIGHT_METRICS.HAS_PRICING]: properties => properties.category === 'pricing',
};

// Which facility types a metric can ever highlight — drives the legend's row set, so we
// never render a row that is empty by construction. `has-pricing` is
// `category === 'pricing'`, which excludes `construction`, so development sites have no
// highlighted row there and their single row means all developments.
export const BY_TYPE_HIGHLIGHTABLE_TYPES = {
    [BY_TYPE_HIGHLIGHT_METRICS.LISTED]: ['operating', 'development'],
    [BY_TYPE_HIGHLIGHT_METRICS.ON_MARKET]: ['operating', 'development'],
    [BY_TYPE_HIGHLIGHT_METRICS.HAS_PRICING]: ['operating'],
};

export default FacilityPinConfig;
